/* eslint-disable camelcase */
import * as React from "react"
import { Alert, Appearance, Platform, TextInput, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Text } from "../../text/text"
import { FastField, Formik } from "formik"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { FormProps } from "../form"
import { TextField } from "../../field/text-field/text-field"
import { PickerField } from "../../field/picker-field/picker-field"
import { FormControl } from "../../form-control/form-control"
import { PhoneInput } from "../../input/phone-input/phone-input"
import { Radio, useDisclose, View } from "native-base"
import * as ImagePicker from 'react-native-image-picker'
import { ListRenderItem } from "@shopify/flash-list"
import { Button } from "../../button/button"
import { PickerActionsheet } from "../../picker-actionsheet/picker-actionsheet"
import { requestCameraAndExternalStoragePermission } from "../../../utils/permissions"
import { useStores } from "../../../models"
import _ from "lodash"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { ModelField } from "../../field/model-field/model-field"
import { Region } from "../../../database"
import { Toast } from "../../toast/toast"
import { color } from "../../../theme"
import { useCallback, useEffect, useRef, useState } from "react"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"


const gender = [{id: 1, name: "man"}, {id: 2, name: "woman"}]

interface Action {
  title: string;
  type: 'capture' | 'library';
  options: ImagePicker.CameraOptions | ImagePicker.ImageLibraryOptions;
}

const actions: Action[] = [
  ...(Platform.OS === 'web' ? [] : [{
    title: 'Take Image',
    type: 'capture',
    options: {
      saveToPhotos: true,
      mediaType: 'photo',
      includeBase64: true,
      // includeBase64: false,
      includeExtra: false,
    },
  } as Action]),
  {
    title: 'Select Image',
    type: 'library',
    options: {
      maxHeight: 200,
      maxWidth: 200,
      selectionLimit: 0,
      mediaType: 'photo',
      includeBase64: true,
      // includeBase64: false,
      includeExtra: false,
    },
  },
];


const FORM_DROPDOWN: ViewStyle = {
  marginBottom: 4,
  paddingHorizontal: 16
}

const ACTION_SHEET: ViewStyle = {
  height: 170,
}

const BUTTON: ViewStyle = {
  alignItems: 'center'
}

const FORM_CONTROL: ViewStyle = {
  marginBottom: 4,
  paddingHorizontal: 16
}

export interface ProfileFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    avatar?: string
    name?: string
    phone?: string
    email?: string
    gender_id?: number
    region_id?: number,
    occupation?: string,
    birthdate?: string
  }
}

const renderItem: ListRenderItem<any> = ({ item }) => {
  // const text = item.title;
  const text = item.type === "capture" ? translate("editProfileForm.takePhoto") : translate("editProfileForm.fromGallery")
  return(
    <View px="16px" py="15px" alignItems="center" justifyContent="center">
      <Text text={text} colorScheme="light" />
    </View>
  )
}

/**
 * Describe your component here
 */
export const ProfileForm = observer(function ProfileForm(props: ProfileFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props
  const [ type, setType ] = useState<"Farmer"|"Non Farmer">()
  const { isOpen, onOpen, onClose } = useDisclose();
  const { userStore, occupationStore, occupationStore: { occupations } } = useStores()

  const input = {
    name: useRef<TextInput>(),
    email: useRef<TextInput>(),
    phone: useRef<TextInput>(),
    gender: useRef<TextInput>(),
    occupation: useRef<TextInput>(),
  }

  const nextInput = {
    name: () => input.email.current && input.email.current.focus(),
    email: () => input.phone.current && input.phone.current.focus(),
    phone: () => input.gender.current && input.gender.current.focus(),
  }

  const insets = useSafeAreaInsets()

  const estimatedHeight = (20 * actions.length)
    + 24 + 32
    + 40 + 7
    // + 60
    + insets.bottom;

  const validationSchema = yup.object({
    avatar: yup.string().nullable().optional(),
    name: yup.string().ascii().nullable().required().label(translate("editProfileForm.name")),
    email: yup.string().email().nullable().required().label(translate("editProfileForm.email")),
    phone: yup.string().nullable().required().label(translate("editProfileForm.phone")),
    gender: yup.number().nullable().label(translate("editProfileForm.gender")),
    occupation: yup.string().ascii().nullable().required().label(translate("registerForm.type")),
    birthdate: yup.mixed().nullable().label(translate("registerForm.birthdate")),
  })

  useEffect(() => {
    const { occupations } = occupationStore
    if(occupations.length && initialValues?.occupation) {
      const occupation = occupations.find((item) => item.code === initialValues?.occupation)
      return !_.isEmpty(occupation) && setType(occupation.type)
    }
  }, [initialValues?.occupation, occupationStore])

  const cameraPermission = useCallback(async (item, setFieldValue) => {
    try {
      const granted = await requestCameraAndExternalStoragePermission()
        .catch(() => false);
      if (granted) {
        if (item) ImagePicker.launchCamera(item.options, (response) => {
          if (response && !response.didCancel && response.assets && response.assets.length) {
            setFieldValue("avatar", response.assets[0].uri)
            userStore.set("responseImage", response)
          }
        }).catch((e) => {
          Toast.show({ id: e.name, title: e.message })
        });
      } else {
        Alert.alert("401", "Camera permission denied")
      }
    } catch (err) {
      console.warn(err);
    }
  }, [userStore]);

  const onPressItem: (item: any, setFieldValue: (field?: string, value?: any, shouldValidate?: boolean ) => void) => any = useCallback((item, setFieldValue) => () => {
    if (item.type === 'capture') {
      cameraPermission(item, setFieldValue);
    } else {
      ImagePicker.launchImageLibrary(item.options, (response) => {
        if (response && !response.didCancel) {
          setFieldValue("avatar", response.assets[0].uri)
          userStore.set("responseImage", response)
        }
      });
    }
  }, [cameraPermission, userStore])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, isValid, setFieldValue, handleSubmit }) => (
        <>
          <Button
            testID="jala:id/photo-button"
            preset="link"
            tx="editProfileForm.photo"
            onPress={onOpen}
            colorScheme={colorScheme}
            style={BUTTON}
          />
          <PickerActionsheet
            colorScheme={colorScheme}
            onClose={onClose}
            isOpen={isOpen}
            data={actions}
            renderItem={renderItem}
            onPressItem={(item) => onPressItem(item, setFieldValue)}
            flatListProps={{
              keyExtractor: (item) => `${item.title}`,
              estimatedItemSize: 20
            }}
            withoutSearch
            actionsheetStyle={{ minHeight: estimatedHeight }}
          />
          <TextField
            name="name"
            txPrefix="editProfileForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{ isRequired: true }}
          />
          <FastField name="email">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`editProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
                colorScheme={colorScheme}
                inputProps={{
                  testID: `jala:id/${field.name}-input`,
                  ref: input[field.name],
                  autoCapitalize: "none",
                  autoComplete: "email",
                  autoCorrect: false,
                  textContentType: "emailAddress",
                  keyboardType: "email-address",
                  returnKeyType: nextInput[field.name] ? "next" : "go",
                  onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                  placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                  defaultValue: meta.initialValue,
                  onChangeText: handleChange(field.name),
                  onBlur: handleBlur(field.name)
                }}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
              />
            )}
          </FastField>
          <FastField name="phone">
            {({ field, form: { handleChange, handleBlur }, meta }) => (
              <FormControl
                isRequired
                isInvalid={meta.touched && !!meta.error}
                labelTx={`editProfileForm.${_.camelCase(field.name)}` as TxKeyPath}
                colorScheme={colorScheme}
                errorMessage={meta.error}
                style={FORM_DROPDOWN}
              >
                <PhoneInput
                  colorScheme={colorScheme}
                  isInvalid={meta.touched && !!meta.error}
                  textProps={{
                    testID: `jala:id/${field.name}-input`,
                    ref: input[field.name],
                    autoCapitalize: "none",
                    autoComplete: "tel",
                    textContentType: "telephoneNumber",
                    returnKeyType: nextInput[field.name] ? "next" : "go",
                    onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
                    placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
                    defaultValue: initialValues.phone,
                    onBlur: handleBlur(field.name),
                  }}
                  initialValue={initialValues.phone}
                  onChangePhoneNumber={handleChange(field.name)}
                />
              </FormControl>
            )}
          </FastField>
          <FormControl
            labelTx="registerForm.type"
            colorScheme={colorScheme}
            labelColor={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
            style={FORM_CONTROL}
            labelPreset="title3"
            isRequired
          >
            <Radio.Group
              colorScheme={colorScheme}
              name="type"
              accessibilityLabel={translate("registerForm.type")}
              value={type}
              onChange={(value: any) => {
                setType(value)
                occupationStore.getOccupations()
                setFieldValue('occupation', null)
              }}
            >
              <Radio value="Farmer" mr="32px" mb="8px">
                <Text tx="registerForm.farmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
              <Radio value="Non Farmer">
                <Text tx="registerForm.nonFarmers" preset="body2" ml="6px" colorScheme={colorScheme} />
              </Radio>
            </Radio.Group>
          </FormControl>
          <PickerField
            key={`${type}-occupation`}
            name="occupation"
            txPrefix="registerForm"
            colorScheme={colorScheme}
            pickerProps={{
              data: occupations.filter((i) => i.type === type),
              dataLabel: "name_translated",
              dataKey: "code",
              titleTx: "registerForm.occupationPlaceholder",
              isDisabled: !type
            }}
          />
          <DateTimePickerField
            name="birthdate"
            txPrefix="registerForm"
            colorScheme={colorScheme}
            formControlProps={{ style: { paddingHorizontal: 16 } }}
          />
          <PickerField
            name="gender"
            txPrefix="editProfileForm"
            colorScheme={colorScheme}
            pickerProps={{
              data: gender,
              renderSelectedItemText: (item) => item && translate(`editProfileForm.${_.camelCase(item.name)}` as TxKeyPath),
              pickerActionsheetProps: { actionsheetStyle: ACTION_SHEET },
              titleTx: "editProfileForm.genderPlaceholder"
            }}
          />
          <ModelField
            name="region_id"
            txPrefix="editProfileForm"
            colorScheme={colorScheme}
            modelPickerProps={{
              Model: Region,
              dataLabel: "full_name",
              titleTx: "editProfileForm.regionIdPlaceholder",
              fetchData: (model, search, params = {}) => {
                return (
                  model.fetch({
                    country_id__eq: 'ID',
                    scope: 'district',
                    search,
                    sort: 'full_name',
                    ...params
                  }).then((models) => {
                    return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                  })
                )
              },
            }}
          />
        </>
      )}
    </Formik>
  )
})

ProfileForm.displayName = 'ProfileForm'
